import {createContext, h} from "preact";
import {useContext, useMemo} from "preact/hooks";
import defaults from "../i18n/defaults";

const TranslationsContext = createContext(defaults);

// Liquid's `t` filter returns this text when a key is absent from the theme's locale file.
const MISSING_TRANSLATION = 'Translation missing:';

function isUsable(value) {
    return typeof value === 'string'
        && value.trim() !== ''
        && ! value.trim().startsWith(MISSING_TRANSLATION);
}

/**
 * Overlays theme-supplied copy on the built-in defaults.
 *
 * Only keys present in `defaults` are read, so the defaults define the shape and the widget cannot
 * be broken by an unexpected payload. Blank and missing values fall back to the default.
 */
function merge(base, override) {
    const merged = { ...base };

    Object.keys(base).forEach(key => {
        const value = override ? override[key] : undefined;

        if (base[key] !== null && typeof base[key] === 'object') {
            merged[key] = merge(base[key], value && typeof value === 'object' ? value : {});
        } else if (isUsable(value)) {
            merged[key] = value;
        }
    });

    return merged;
}

export function TranslationsProvider({ translations, children }) {
    const value = useMemo(() => merge(defaults, translations), [translations]);

    return (
        <TranslationsContext.Provider value={value}>
            {children}
        </TranslationsContext.Provider>
    );
}

export function useTranslations() {
    return useContext(TranslationsContext);
}

export default TranslationsContext;
